/**
 * Budget mutation hooks (Phase 3, §6.2 / §6.6). The Budget page reads spend-vs-
 * budget from `GET /api/reports/month` (`useReportMonth`), so a budget edit
 * optimistically patches THAT cache (`['reports','month',m]`) and rolls back on
 * error; on settle it invalidates `['budgets']` + the month report.
 */
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { api } from '../api.ts';
import { qk } from '../queryKeys.ts';
import { useToast } from '../../context/ToastContext.tsx';
import { patchMonthViewBudget } from '../../pages/budget/budgetMath.ts';
import type {
  BudgetCopyIn,
  BudgetCopyResult,
  BudgetOut,
  Money,
  MonthStr,
  MonthViewOut,
} from '../types.ts';

export interface UpsertBudgetVars {
  bucket_id: number;
  /** `null` clears the bucket's budget for the month. */
  amount: Money | null;
}

interface UpsertContext {
  previous: MonthViewOut | undefined;
}

/**
 * PUT /api/budgets?month=… — upsert (or clear) one bucket's budget for `month`,
 * optimistically patching the month-view cache.
 */
export function useUpsertBudget(
  month: MonthStr,
): UseMutationResult<BudgetOut[], Error, UpsertBudgetVars, UpsertContext> {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation<BudgetOut[], Error, UpsertBudgetVars, UpsertContext>({
    mutationFn: ({ bucket_id, amount }) =>
      api.put<BudgetOut[]>(`/api/budgets?month=${month}`, {
        items: [{ bucket_id, amount }],
      }),
    onMutate: async ({ bucket_id, amount }) => {
      await qc.cancelQueries({ queryKey: qk.reportMonth(month) });
      const previous = qc.getQueryData<MonthViewOut>(qk.reportMonth(month));
      if (previous) {
        qc.setQueryData<MonthViewOut>(
          qk.reportMonth(month),
          patchMonthViewBudget(previous, bucket_id, amount),
        );
      }
      return { previous };
    },
    onError: (error, _vars, context) => {
      if (context?.previous) {
        qc.setQueryData(qk.reportMonth(month), context.previous);
      }
      toast(
        error instanceof Error ? error.message : 'Budget konnte nicht gespeichert werden.',
        { variant: 'error' },
      );
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: qk.reportMonth(month) });
      void qc.invalidateQueries({ queryKey: qk.budgets });
    },
  });
}

/** POST /api/budgets/copy — "Vormonat kopieren". Invalidates the target month. */
export function useCopyBudgets(): UseMutationResult<BudgetCopyResult, Error, BudgetCopyIn> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BudgetCopyIn) => api.post<BudgetCopyResult>('/api/budgets/copy', body),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: qk.reportMonth(result.to_month) });
      void qc.invalidateQueries({ queryKey: qk.budgets });
    },
  });
}
